import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Print,
  QrCode,
  LocationOn,
  CalendarToday,
  Person,
  Business,
  Science,
  Assignment,
  CheckCircle,
  Warning,
  AccessTime,
  PlayArrow,
  Download,
  Share,
  Star,
  StarBorder,
  Visibility,
  Home,
  Map,
  Analytics,
  History,
  AttachFile,
  Comment,
  Notifications,
  TrendingUp,
  Timeline,
  Speed,
  Inventory2,
  PrecisionManufacturing,
  Psychology,
  Whatshot,
  Diamond,
  InvertColors,
  GppGood,
  MenuBook,
  TaskAlt,
  Build,
  LocalPharmacy,
  Flare,
  AutoGraph,
  Stars,
  WaterDrop,
  ExpandMore,
  MonitorHeart,
  NetworkCheck,
  Schema,
  DeviceHub,
  Radar,
  ElectricBolt,
  EmojiEvents,
  Verified,
  Security,
  Shield,
  Lock,
  VpnKey,
  Badge as BadgeIcon,
  MilitaryTech,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAuth, UserRole } from '../../contexts/AuthContext';
import samplesService, { Sample, SampleStatus, Priority } from '../../services/samplesService';

const SampleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const theme = useTheme();
  
  const [sample, setSample] = useState<Sample | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<SampleStatus | ''>('');
  const [workflowNotes, setWorkflowNotes] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['general', 'analysis', 'workflow']);

  useEffect(() => {
    if (id) {
      loadSample();
    }
  }, [id]);

  const loadSample = async () => {
    try {
      setLoading(true);
      setError(null);
      const sampleData = await samplesService.getSample(id!);
      setSample(sampleData);
    } catch (error) {
      console.error('Error loading sample:', error);
      setError('Erreur lors du chargement de l\'échantillon');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: SampleStatus) => {
    switch (status) {
      case SampleStatus.RECEPTION: return 'info';
      case SampleStatus.PREPARATION_MECANIQUE: return 'warning';
      case SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE: return 'secondary';
      case SampleStatus.LABORATOIRE_SAA_ANALYSE: return 'primary';
      case SampleStatus.VALIDATION_SERVICE: return 'success';
      case SampleStatus.GENERATION_CERTIFICAT: return 'info';
      case SampleStatus.PRET: return 'success';
      case SampleStatus.EN_ATTENTE: return 'warning';
      case SampleStatus.REJETE: return 'error';
      default: return 'default';
    }
  };

  // Function removed - was unused

  const getStatusLabel = (status: SampleStatus): string => {
    switch (status) {
      case SampleStatus.RECEPTION: return 'Réception';
      case SampleStatus.PREPARATION_MECANIQUE: return 'Préparation Mécanique';
      case SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE: return 'SAA - Préparation Chimique';
      case SampleStatus.LABORATOIRE_SAA_ANALYSE: return 'SAA - Analyse';
      case SampleStatus.VALIDATION_SERVICE: return 'Validation Service';
      case SampleStatus.GENERATION_CERTIFICAT: return 'Génération Certificat';
      case SampleStatus.PRET: return 'Prêt';
      case SampleStatus.EN_ATTENTE: return 'En Attente';
      case SampleStatus.REJETE: return 'Rejeté';
      default: return status;
    }
  };

  const getProgressPercentage = (status: SampleStatus): number => {
    switch (status) {
      case SampleStatus.RECEPTION: return 10;
      case SampleStatus.PREPARATION_MECANIQUE: return 25;
      case SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE: return 40;
      case SampleStatus.LABORATOIRE_SAA_ANALYSE: return 60;
      case SampleStatus.VALIDATION_SERVICE: return 80;
      case SampleStatus.GENERATION_CERTIFICAT: return 90;
      case SampleStatus.PRET: return 100;
      case SampleStatus.EN_ATTENTE: return 50;
      case SampleStatus.REJETE: return 0;
      default: return 0;
    }
  };

  const handleStatusChange = async () => {
    if (!sample || !newStatus) return;

    try {
      setLoading(true);
      await samplesService.updateSampleStatus(sample.id, newStatus as SampleStatus, workflowNotes);
      await loadSample();
      setWorkflowDialogOpen(false);
      setNewStatus('');
      setWorkflowNotes('');
    } catch (error) {
      console.error('Error updating sample status:', error);
      setError('Erreur lors de la mise à jour du statut');
    } finally {
      setLoading(false);
    }
  };

  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const getAnalysisResults = () => {
    // Try SAA analysis form first
    const saaReadings = sample?.metadonnees?.saa_analysis_form?.analysis_readings;
    if (saaReadings && Array.isArray(saaReadings) && saaReadings.length > 0) {
      const results: Record<string, any> = {};
      saaReadings.forEach((reading: any) => {
        if (reading.element) {
          results[reading.element] = {
            ppm: reading.concentration_ppm || 0,
            percent: reading.concentration_final_percent || 0,
            absorbance: reading.absorbance || 0,
            uncertainty: reading.uncertainty || 0,
            wavelength: reading.instrument_settings?.wavelength || 'N/A',
          };
        }
      });
      return Object.keys(results).length > 0 ? results : null;
    }

    // Try general analysis results as fallback
    const generalResults = sample?.metadonnees?.analysis_results;
    if (generalResults && Array.isArray(generalResults) && generalResults.length > 0) {
      const results: Record<string, any> = {};
      generalResults.forEach((result: any) => {
        const element = result.element || result.parameter;
        if (element) {
          results[element] = {
            ppm: result.concentration_ppm || result.value || 0,
            percent: result.concentration_percent || (result.value ? result.value / 10000 : 0),
            absorbance: result.absorbance || 0,
            uncertainty: result.uncertainty || 0,
            wavelength: 'N/A',
          };
        }
      });
      return Object.keys(results).length > 0 ? results : null;
    }

    return null;
  };

  const workflowSteps = [
    { status: SampleStatus.RECEPTION, label: 'Réception', icon: <Inventory2 />, color: 'info' },
    { status: SampleStatus.PREPARATION_MECANIQUE, label: 'Préparation', icon: <Build />, color: 'warning' },
    { status: SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE, label: 'SAA Prep', icon: <LocalPharmacy />, color: 'secondary' },
    { status: SampleStatus.LABORATOIRE_SAA_ANALYSE, label: 'SAA Analyse', icon: <Whatshot />, color: 'primary' },
    { status: SampleStatus.VALIDATION_SERVICE, label: 'Validation', icon: <Verified />, color: 'success' },
    { status: SampleStatus.GENERATION_CERTIFICAT, label: 'Certificat', icon: <MenuBook />, color: 'info' },
    { status: SampleStatus.PRET, label: 'Terminé', icon: <EmojiEvents />, color: 'success' },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error || !sample) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Échantillon non trouvé'}</Alert>
        <Button onClick={() => navigate('/samples')} variant="contained" startIcon={<ArrowBack />}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  const analysisResults = getAnalysisResults();

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* SPECTACULAR HEADER */}
      <Card sx={{ 
        mb: 3, 
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="2" cy="2" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          zIndex: 0,
        }} />
        
        <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <IconButton 
                onClick={() => navigate('/samples')} 
                sx={{ color: 'white', bgcolor: alpha(theme.palette.common.white, 0.1) }}
              >
            <ArrowBack />
          </IconButton>
              <Avatar sx={{ 
                bgcolor: alpha(theme.palette.common.white, 0.2), 
                width: 80, 
                height: 80,
                border: `3px solid ${alpha(theme.palette.common.white, 0.3)}`
              }}>
                <Science sx={{ fontSize: 40 }} />
          </Avatar>
          <Box>
                <Typography variant="h3" fontWeight={800} sx={{ mb: 1 }}>
              {sample.sample_id}
            </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Chip 
                label={getStatusLabel(sample.statut)} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      color: 'white',
                      fontWeight: 600
                    }}
              />
              <Chip 
                label={sample.priorite} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15),
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                  <Chip 
                    label={sample.type_echantillon} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.1),
                      color: 'white',
                      fontWeight: 600
                    }}
              />
            </Box>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  {sample.client?.company_name} • {sample.external_id || 'Réf. Client N/A'}
                </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                onClick={() => setIsFavorite(!isFavorite)}
                sx={{ color: 'white' }}
              >
                {isFavorite ? <Star /> : <StarBorder />}
          </IconButton>
              <Button 
                variant="outlined" 
                startIcon={<QrCode />}
                sx={{ color: 'white', borderColor: 'white' }}
              >
            QR Code
          </Button>
              <Button 
                variant="outlined" 
                startIcon={<Print />}
                sx={{ color: 'white', borderColor: 'white' }}
              >
            Imprimer
          </Button>
              <Button 
                variant="outlined" 
                startIcon={<Share />}
                sx={{ color: 'white', borderColor: 'white' }}
              >
            Partager
          </Button>
        </Box>
      </Box>

          {/* PROGRESS BAR */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Progression: {getProgressPercentage(sample.statut)}%
          </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {sample.date_reception ? 
                  `${Math.ceil((Date.now() - new Date(sample.date_reception).getTime()) / (1000 * 60 * 60 * 24))} jours` 
                  : 'Temps inconnu'
                }
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={getProgressPercentage(sample.statut)} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: alpha(theme.palette.common.white, 0.2),
                '& .MuiLinearProgress-bar': {
                  bgcolor: alpha(theme.palette.common.white, 0.8),
                  borderRadius: 4
                }
              }}
            />
          </Box>

          {/* KEY METRICS */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.common.white, 0.1), borderRadius: 2 }}>
                <CalendarToday sx={{ fontSize: 24, mb: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Date Réception</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {sample.date_reception ? new Date(sample.date_reception).toLocaleDateString('fr-FR') : 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.common.white, 0.1), borderRadius: 2 }}>
                <LocationOn sx={{ fontSize: 24, mb: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Site Minier</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {sample.site_minier || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.common.white, 0.1), borderRadius: 2 }}>
                <Assignment sx={{ fontSize: 24, mb: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Projet</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {sample.nom_projet || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.common.white, 0.1), borderRadius: 2 }}>
                <Speed sx={{ fontSize: 24, mb: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Priorité</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {sample.priorite}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* MAIN CONTENT */}
      <Grid container spacing={3}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} lg={8}>
          {/* ANALYSIS RESULTS */}
          <Accordion 
            expanded={expandedSections.includes('analysis')}
            onChange={() => handleSectionToggle('analysis')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <Analytics />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Résultats d'Analyse SAA
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analysisResults ? 'Données disponibles' : 'Aucune donnée d\'analyse'}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
                         <AccordionDetails>
               {analysisResults ? (
                 <>
                   {/* ENHANCED RESULTS GRID */}
                   <Grid container spacing={3} sx={{ mb: 4 }}>
                     {Object.entries(analysisResults).map(([element, data]: [string, any]) => (
                       <Grid item xs={12} sm={6} md={4} key={element}>
                         <Card sx={{ 
                           p: 3, 
                           height: '100%',
                           border: `2px solid ${theme.palette.primary.main}`,
                           bgcolor: alpha(theme.palette.primary.main, 0.05),
                           transition: 'transform 0.2s, box-shadow 0.2s',
                           '&:hover': {
                             transform: 'translateY(-4px)',
                             boxShadow: theme.shadows[8]
                           }
                         }}>
                           <Box sx={{ textAlign: 'center', mb: 2 }}>
                             <Avatar sx={{ 
                               width: 48, 
                               height: 48, 
                               bgcolor: 'primary.main',
                               mx: 'auto',
                               mb: 1,
                               fontSize: '1.5rem',
                               fontWeight: 800
                             }}>
                               {element}
                             </Avatar>
                             <Typography variant="h6" fontWeight={700} color="primary.main">
                               {element}
                             </Typography>
                           </Box>
                           
                           <Divider sx={{ my: 2 }} />
                           
                           <List dense>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemText 
                                 primary="Concentration (ppm)"
                                 secondary={
                                   <Typography variant="h6" fontWeight={600} color="primary.main">
                                     {data.ppm?.toFixed(3) || '0.000'}
                                   </Typography>
                                 }
                               />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemText 
                                 primary="Concentration (%)"
                                 secondary={
                                   <Typography variant="h6" fontWeight={600} color="secondary.main">
                                     {data.percent?.toFixed(4) || '0.0000'}
                                   </Typography>
                                 }
                               />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemText 
                                 primary="Absorbance"
                                 secondary={data.absorbance?.toFixed(4) || 'N/A'}
                               />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemText 
                                 primary="Longueur d'onde"
                                 secondary={`${data.wavelength} nm`}
                               />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemText 
                                 primary="Incertitude (±)"
                                 secondary={data.uncertainty?.toFixed(3) || '0.000'}
                               />
                             </ListItem>
                           </List>
                         </Card>
                       </Grid>
                     ))}
                   </Grid>

                   {/* ANALYSIS SUMMARY TABLE */}
                   <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                     Résumé des Analyses
                   </Typography>
                   <TableContainer component={Card} variant="outlined">
                     <Table>
                       <TableHead>
                         <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                           <TableCell sx={{ fontWeight: 700 }}>Élément</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 700 }}>Absorbance</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 700 }}>Concentration (ppm)</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 700 }}>Concentration (%)</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 700 }}>Longueur d'onde (nm)</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 700 }}>Incertitude (±)</TableCell>
                         </TableRow>
                       </TableHead>
                       <TableBody>
                         {Object.entries(analysisResults).map(([element, data]: [string, any]) => (
                           <TableRow key={element} hover>
                             <TableCell>
                               <Chip 
                                 label={element} 
                                 color="primary" 
                                 variant="outlined"
                                 sx={{ fontWeight: 600 }}
                               />
                             </TableCell>
                             <TableCell align="right">
                               <Typography variant="body2" fontFamily="monospace">
                                 {data.absorbance?.toFixed(4) || 'N/A'}
                               </Typography>
                             </TableCell>
                             <TableCell align="right">
                               <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                                 {data.ppm?.toFixed(3) || '0.000'}
                               </Typography>
                             </TableCell>
                             <TableCell align="right">
                               <Typography variant="body2" fontWeight={600} fontFamily="monospace" color="secondary.main">
                                 {data.percent?.toFixed(4) || '0.0000'}
                               </Typography>
                             </TableCell>
                             <TableCell align="right">
                               <Typography variant="body2" fontFamily="monospace">
                                 {data.wavelength}
                               </Typography>
                             </TableCell>
                             <TableCell align="right">
                               <Typography variant="body2" fontFamily="monospace">
                                 {data.uncertainty?.toFixed(3) || '0.000'}
                               </Typography>
                             </TableCell>
                           </TableRow>
                         ))}
                       </TableBody>
                     </Table>
                   </TableContainer>

                   {/* ANALYSIS METADATA */}
                   {sample.metadonnees?.saa_analysis_form && (
                     <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                       <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Science color="info" />
                         Métadonnées d'Analyse
                       </Typography>
                       <Grid container spacing={2}>
                         <Grid item xs={12} sm={6} md={3}>
                           <Typography variant="body2" color="text.secondary">Analyste</Typography>
                           <Typography variant="body1" fontWeight={600}>
                             {sample.metadonnees?.saa_analyst_name || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={12} sm={6} md={3}>
                           <Typography variant="body2" color="text.secondary">Date d'analyse</Typography>
                           <Typography variant="body1" fontWeight={600}>
                             {sample.metadonnees?.saa_analysis_completed_at ? 
                               new Date(sample.metadonnees?.saa_analysis_completed_at).toLocaleDateString('fr-FR') 
                               : 'N/A'
                             }
                           </Typography>
                         </Grid>
                         <Grid item xs={12} sm={6} md={3}>
                           <Typography variant="body2" color="text.secondary">Coefficient R²</Typography>
                           <Typography variant="body1" fontWeight={600}>
                             {sample.metadonnees?.saa_analysis_form?.instrument_status?.r_squared?.toFixed(4) || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={12} sm={6} md={3}>
                           <Typography variant="body2" color="text.secondary">Statut</Typography>
                           <Chip 
                             label={sample.metadonnees?.saa_analysis_form?.status || 'UNKNOWN'}
                             size="small"
                             color="success"
                           />
                         </Grid>
                       </Grid>
                     </Box>
                   )}
                 </>
               ) : (
                 <Box sx={{ textAlign: 'center', py: 8 }}>
                   <Avatar sx={{ 
                     width: 80, 
                     height: 80, 
                     bgcolor: alpha(theme.palette.warning.main, 0.1),
                     mx: 'auto',
                     mb: 2
                   }}>
                     <Analytics sx={{ fontSize: 40, color: 'warning.main' }} />
                   </Avatar>
                   <Typography variant="h5" fontWeight={600} color="warning.main" sx={{ mb: 1 }}>
                     Aucun Résultat d'Analyse
                   </Typography>
                   <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                     Les résultats d'analyse apparaîtront ici une fois l'analyse SAA terminée
                   </Typography>
                   <Typography variant="body2" color="text.secondary">
                     Statut actuel: {getStatusLabel(sample.statut)}
                   </Typography>
                 </Box>
               )}
             </AccordionDetails>
          </Accordion>

          {/* WORKFLOW VISUALIZATION */}
          <Accordion 
            expanded={expandedSections.includes('workflow')}
            onChange={() => handleSectionToggle('workflow')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                  <Timeline />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Workflow de Traitement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Suivi détaillé du processus de traitement
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
          <Stepper 
            activeStep={workflowSteps.findIndex(step => step.status === sample.statut)} 
                orientation="vertical"
          >
            {workflowSteps.map((step, index) => {
              const isCompleted = getProgressPercentage(sample.statut) > getProgressPercentage(step.status);
              const isCurrent = sample.statut === step.status;
              
              return (
                <Step key={step.status} completed={isCompleted}>
                  <StepLabel
                        icon={
                          <Avatar sx={{ 
                          width: 48,
                          height: 48,
                          bgcolor: isCompleted || isCurrent ? `${step.color}.main` : 'grey.300',
                            color: 'white'
                          }}>
                        {step.icon}
                          </Avatar>
                        }
                  >
                        <Typography variant="h6" fontWeight={600}>
                      {step.label}
                    </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {isCurrent ? 'En cours de traitement' : isCompleted ? 'Étape terminée' : 'En attente'}
                    </Typography>
                        {isCurrent && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="indeterminate" 
                              sx={{ width: 200, height: 4, borderRadius: 2 }}
                            />
                          </Box>
                        )}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
          
          {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.TECHNICIAN_PREP, UserRole.ANALYST]) && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button 
                variant="contained" 
                    size="large"
                startIcon={<PlayArrow />}
                onClick={() => setWorkflowDialogOpen(true)}
              >
                Changer le Statut
              </Button>
            </Box>
          )}
            </AccordionDetails>
          </Accordion>

          {/* TECHNICAL DETAILS */}
          <Accordion 
            expanded={expandedSections.includes('technical')}
            onChange={() => handleSectionToggle('technical')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                  <Science />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Détails Techniques
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Métadonnées et informations techniques
                  </Typography>
        </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QrCode />
                        Codes de Traçabilité
                  </Typography>
                  <List dense>
                    <ListItem>
                          <ListItemIcon><QrCode /></ListItemIcon>
                          <ListItemText primary="QR Code" secondary={sample.qr_code} />
                    </ListItem>
                    <ListItem>
                          <ListItemIcon><Assignment /></ListItemIcon>
                          <ListItemText primary="Code Barres" secondary={sample.code_barres} />
                    </ListItem>
                    <ListItem>
                          <ListItemIcon><LocationOn /></ListItemIcon>
                          <ListItemText primary="Localisation" secondary={sample.localisation_stockage || 'Non définie'} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Map />
                        Coordonnées
                  </Typography>
                      {sample.coordonnees ? (
                  <List dense>
                    <ListItem>
                            <ListItemText primary="Latitude" secondary={`${sample.coordonnees.latitude}°`} />
                    </ListItem>
                    <ListItem>
                            <ListItemText primary="Longitude" secondary={`${sample.coordonnees.longitude}°`} />
                    </ListItem>
                    <ListItem>
                            <ListItemText primary="Élévation" secondary={`${sample.coordonnees.elevation || 'N/A'} m`} />
                    </ListItem>
                  </List>
                      ) : (
                        <Typography color="text.secondary">Aucune coordonnée disponible</Typography>
                      )}
                </CardContent>
              </Card>
            </Grid>
                    </Grid>
            </AccordionDetails>
          </Accordion>
                    </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} lg={4}>
          {/* CLIENT INFORMATION */}
          <Card sx={{ mb: 3 }}>
                <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business />
                Informations Client
                  </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><Business /></ListItemIcon>
                  <ListItemText primary="Société" secondary={sample.client?.company_name} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText primary="Contact" secondary={sample.client?.contact_person || 'N/A'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><LocationOn /></ListItemIcon>
                  <ListItemText primary="Email" secondary={sample.client?.email} />
                </ListItem>
              </List>
                </CardContent>
              </Card>

          {/* IMPORTANT DATES */}
          <Card sx={{ mb: 3 }}>
                <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday />
                Dates Importantes
                  </Typography>
                  <List dense>
                    <ListItem>
                  <ListItemIcon><Inventory2 /></ListItemIcon>
                      <ListItemText 
                    primary="Réception" 
                    secondary={sample.date_reception ? new Date(sample.date_reception).toLocaleDateString('fr-FR') : 'N/A'} 
                      />
                    </ListItem>
                    <ListItem>
                  <ListItemIcon><Build /></ListItemIcon>
                      <ListItemText 
                    primary="Début Préparation" 
                    secondary={sample.date_debut_preparation ? new Date(sample.date_debut_preparation).toLocaleDateString('fr-FR') : 'N/A'} 
                      />
                    </ListItem>
                    <ListItem>
                  <ListItemIcon><Analytics /></ListItemIcon>
                      <ListItemText 
                    primary="Début Analyse" 
                    secondary={sample.date_debut_analyse ? new Date(sample.date_debut_analyse).toLocaleDateString('fr-FR') : 'N/A'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle /></ListItemIcon>
                  <ListItemText 
                    primary="Date Prêt" 
                    secondary={sample.date_pret ? new Date(sample.date_pret).toLocaleDateString('fr-FR') : 'N/A'} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

          {/* QUICK STATS */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp />
                Statistiques Rapides
              </Typography>
                <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="primary.main">
                      {getProgressPercentage(sample.statut)}%
              </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Progression
              </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="success.main">
                      {sample.date_reception ? 
                        Math.ceil((Date.now() - new Date(sample.date_reception).getTime()) / (1000 * 60 * 60 * 24))
                        : 0
                      }
              </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Jours
              </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* WORKFLOW DIALOG */}
      <Dialog open={workflowDialogOpen} onClose={() => setWorkflowDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrow color="primary" />
            Changer le Statut - {sample.sample_id}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Statut actuel: <Chip label={getStatusLabel(sample.statut)} size="small" />
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Nouveau Statut</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as SampleStatus)}
                label="Nouveau Statut"
              >
                {Object.values(SampleStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={getStatusLabel(status)} size="small" color={getStatusColor(status)} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes et commentaires"
              value={workflowNotes}
              onChange={(e) => setWorkflowNotes(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Ajoutez des notes sur ce changement de statut..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWorkflowDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleStatusChange} 
            variant="contained"
            disabled={!newStatus || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {loading ? 'Mise à jour...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SampleDetail; 